import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '@/lib/utils';

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

export function MarkdownRenderer({ content, className }: MarkdownRendererProps) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      className={cn('prose prose-sm dark:prose-invert max-w-full break-words [overflow-wrap:anywhere] overflow-x-hidden', className)}
      components={{
        pre: ({ children }) => (
          <pre className="bg-muted rounded-md p-3 overflow-x-auto text-xs my-2">{children}</pre>
        ),
        code: ({ children, className: codeClassName }) => {
          const isInline = !codeClassName;
          if (isInline) {
            return (
              <code className="bg-muted rounded px-1 py-0.5 text-xs font-mono">{children}</code>
            );
          }
          return <code className="text-xs font-mono">{children}</code>;
        },
        table: ({ children }) => (
          <div className="w-full overflow-x-auto my-2 max-w-full">
            <table className="border-collapse border border-border text-xs w-full table-fixed">
              {children}
            </table>
          </div>
        ),
        th: ({ children }) => (
          <th className="border border-border bg-muted px-2 py-1 text-left font-medium whitespace-normal break-words [overflow-wrap:anywhere]">
            {children}
          </th>
        ),
        td: ({ children }) => (
          <td className="border border-border px-2 py-1 whitespace-normal break-words [overflow-wrap:anywhere]">{children}</td>
        ),
        a: ({ children, href }) => (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline hover:opacity-80"
          >
            {children}
          </a>
        ),
        ul: ({ children }) => <ul className="list-disc pl-4 my-1 space-y-0.5">{children}</ul>,
        ol: ({ children }) => <ol className="list-decimal pl-4 my-1 space-y-0.5">{children}</ol>,
        p: ({ children }) => {
          const text = React.Children.toArray(children).join('');
          const hasTabularSpacing = typeof text === 'string' && /\t/.test(text);
          if (hasTabularSpacing) {
            return (
              <div className="overflow-x-auto my-1">
                <pre className="text-xs whitespace-pre" style={{ margin: 0 }}>{text}</pre>
              </div>
            );
          }
          return <p className="my-1">{children}</p>;
        },
        h1: ({ children }) => <h1 className="text-base font-bold mt-3 mb-1">{children}</h1>,
        h2: ({ children }) => <h2 className="text-sm font-bold mt-2 mb-1">{children}</h2>,
        h3: ({ children }) => <h3 className="text-sm font-semibold mt-2 mb-1">{children}</h3>,
      }}
    >
      {content}
    </ReactMarkdown>
  );
}
